import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { ArrowRight, Cpu, Zap, Box, Code, Layers } from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { intelligentProductsFAQs } from "@/data/faqData"

export default function IntelligentProducts() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Intelligent Products | Next-Gen Software Engineering | RSA Tech Group"
        description="Engineering next-gen software infused with generative AI capabilities and self-learning loops. Products that think and adapt."
        image="https://images.unsplash.com/photo-1555664424-778a69022365?auto=format&fit=crop&q=80&w=2000"
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Intelligent <span className="text-accent">Products</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Engineering next-gen software infused with generative AI
            capabilities and self-learning loops.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Products That Think
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                In the age of AI, a static product is an obsolete product. We
                help you build intelligent applications that constantly analyze
                usage patterns, optimize their own performance, and deliver
                proactive value to users.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From smart IoT devices to AI-powered SaaS platforms, we infuse
                cognitive capabilities into the DNA of your product, turning
                simple tools into indispensable assistants.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: Code, text: "Generative Code Integration" },
                  { icon: Zap, text: "Real-time Personalization Engines" },
                  { icon: Box, text: "Edge AI & IoT Deployment" },
                  { icon: Layers, text: "Self-Optimizing Features" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1555664424-778a69022365?auto=format&fit=crop&q=80&w=2000"
                alt="Intelligent Hardware and Code"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6">
              Engineering Excellence
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Smart IoT",
                desc: "Connected devices that process data at the edge for instant decision making.",
                img: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f",
              },
              {
                title: "AI-Powered SaaS",
                desc: "Web applications that use LLMs to automate complex user workflows.",
                img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
              },
              {
                title: "Generative Design",
                desc: "Using AI to create optimal product designs and feature sets automatically.",
                img: "https://images.unsplash.com/photo-1535378437327-b7102b65633b",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group rounded-xl overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.img + "?auto=format&fit=crop&q=80&w=800"}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
              >
                Build Your Product <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PageFAQ
        faqs={intelligentProductsFAQs}
        title="Intelligent Products FAQs"
        subtitle="Common questions about AI-powered product development"
        variant="light"
      />

      <Footer />
    </div>
  )
}
